import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { ASSETS } from "@/data";

export const Hero = ({ onReserve, shipDate }) => {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background media: fanned cards <-> zooming video, looping crossfade */}
      <div className="absolute inset-0 bg-sf-bg">
        {/* Fanned cards layer (opens each loop) */}
        <motion.img
          src={ASSETS.fannedCards}
          alt="A hand fanning out the Sacred Frontline oracle cards"
          className="absolute inset-0 w-full h-full object-cover origin-bottom"
          animate={{
            opacity: [1, 0, 0, 1],
            scale: [1.12, 1.06, 1.06, 1.12],
            rotate: [-5, 0, 0, -5],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Zooming video layer */}
        <motion.video
          className="absolute inset-0 w-full h-full object-cover"
          src={ASSETS.heroVideo}
          autoPlay
          muted
          loop
          playsInline
          poster={ASSETS.boxMockup}
          animate={{
            opacity: [0, 1, 1, 0],
            scale: [1.0, 1.05, 1.14, 1.0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-sf-bg/80 via-sf-bg/55 to-sf-bg" />
      <div className="absolute inset-0 bg-gradient-to-r from-sf-bg via-sf-bg/70 to-sf-bg/20" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(11,11,12,0.85),transparent_60%)]" />
      <div className="absolute inset-0 sf-scanlines opacity-40" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="inline-flex items-center gap-2 border border-sf-ember/60 bg-sf-bg/70 backdrop-blur-md text-sf-emberBright rounded-sm px-3.5 py-2 mb-6 shadow-[0_2px_20px_rgba(0,0,0,0.6)]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-sf-ember animate-pulse" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-sf-bone">Limited First Printing</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="sf-stencil text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.9] text-sf-bone max-w-4xl [text-shadow:0_4px_30px_rgba(0,0,0,0.7)]"
        >
          Sacred<br />
          <span className="sf-ember-text">Frontline</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-6 text-lg md:text-2xl text-sf-bone/95 font-normal max-w-2xl tracking-wide [text-shadow:0_2px_18px_rgba(0,0,0,0.9)]"
        >
          Messages for the protectors, the wounded, and the awakened.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <span className="relative inline-flex">
            {/* pulsing attention ring */}
            <motion.span
              aria-hidden="true"
              className="absolute inset-0 rounded-sm bg-sf-ember"
              animate={{ opacity: [0.5, 0], scale: [1, 1.35] }}
              transition={{ repeat: Infinity, duration: 1.8, ease: "easeOut" }}
            />
            <motion.button
              data-testid="hero-cta-button"
              onClick={onReserve}
              animate={{
                scale: [1, 1.035, 1],
                boxShadow: [
                  "0 0 12px rgba(210,84,51,0.4)",
                  "0 0 40px rgba(232,112,58,0.85)",
                  "0 0 12px rgba(210,84,51,0.4)",
                ],
              }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              whileHover={{ scale: 1.06, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group relative inline-flex items-center gap-2.5 bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-[0.15em] text-lg md:text-xl px-11 py-5 rounded-sm ring-2 ring-sf-emberBright/60"
            >
              Reserve My Deck — Free
              <ArrowRight size={22} className="transition-transform group-hover:translate-x-1.5" />
            </motion.button>
          </span>
          <button
            data-testid="hero-secondary-link"
            onClick={() => scrollTo("#whats-inside")}
            className="font-mono text-sm uppercase tracking-[0.2em] text-sf-bone/80 hover:text-sf-bone border-b border-transparent hover:border-sf-ember pb-1 transition-colors"
          >
            See what’s inside →
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-sf-dim [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]"
        >
          Pre-order now — first-wave decks ship {shipDate}.
        </motion.p>
      </div>

      <motion.button
        onClick={() => scrollTo("#mission")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-sf-dim hover:text-sf-ember transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        aria-label="Scroll down"
      >
        <ChevronDown size={28} />
      </motion.button>
    </section>
  );
};

export default Hero;
